"use client";

import { cn } from "@/lib/utils";

import { useCalendarContext } from "../calendar-context";
import { getEventColorClasses } from "../event-colors";
import { type WeekSegment } from "../event-layout";
import { formatTime } from "../utils";

import { EventPopover } from "./event-popover";

// =============================================================================

type EventBarProps = {
  segment: WeekSegment;
  /** Right-aligned start time (timed multi-day events) */
  showTime: boolean;
  /** Below sm the bar turns into a plain color strip (narrow month cells) */
  compactOnMobile?: boolean;
};

/**
 * Horizontal bar spanning the days it covers: multi-day events of the month
 * view and the all-day strip of the week view. Placed by its parent's grid.
 */
export function EventBar({
  segment,
  showTime,
  compactOnMobile = false,
}: EventBarProps) {
  const { locale } = useCalendarContext();
  const { event, startCol, endCol, lane, isStart, isEnd } = segment;

  return (
    <EventPopover event={event}>
      <button
        type="button"
        className={cn(
          "pointer-events-auto relative flex h-5.5 min-w-0 items-center gap-1 border px-1.5 text-left text-xs font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
          // Squared, borderless sides: the bar continues on another row
          isStart ? "ml-1 rounded-l-md" : "border-l-0",
          isEnd ? "mr-1 rounded-r-md" : "border-r-0",
          getEventColorClasses(event.color).base
        )}
        style={{
          gridColumn: `${startCol + 1} / span ${endCol - startCol + 1}`,
          gridRow: lane + 1,
        }}
      >
        <span className={cn("truncate", compactOnMobile && "max-sm:sr-only")}>
          {event.title}
        </span>
        {showTime && (
          <span
            className={cn(
              "ml-auto shrink-0 font-normal opacity-80",
              compactOnMobile && "max-sm:hidden"
            )}
          >
            {formatTime(event.start, locale)}
          </span>
        )}
      </button>
    </EventPopover>
  );
}
